import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Stack, Paper, Typography, Button } from '@mui/material';
import { Check, PartyPopper } from 'lucide-react';

export const AssessmentComplete: React.FC = () => {
  const history = useHistory();

  const stopAllMediaStreams = () => {
    // Get all video and audio elements
    const videos = document.querySelectorAll('video');
    const audios = document.querySelectorAll('audio');

    // Stop all video streams
    videos.forEach((video) => {
      if (video.srcObject) {
        const stream = video.srcObject as MediaStream;
        stream.getTracks().forEach((track) => {
          track.stop();
        });
        video.srcObject = null;
      }
    });

    // Stop all audio streams
    audios.forEach((audio) => {
      if (audio.srcObject) {
        const stream = audio.srcObject as MediaStream;
        stream.getTracks().forEach((track) => {
          track.stop();
        });
        audio.srcObject = null;
      }
    });
  };

  // Stop all media streams when component mounts
  useEffect(() => {
    stopAllMediaStreams();

    // Cleanup on unmount
    return () => {
      stopAllMediaStreams();
    };
  }, []);

  const logOut = () => {
    // Clean up all media streams before logging out
    stopAllMediaStreams();
    localStorage.clear();
    history.push('/login');
  };

  const firstName = localStorage.getItem('firstname');

  return (
    <Box
      sx={{
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, md: 4 },
      }}
    >
      <Paper
        elevation={0}
        variant='outlined'
        sx={{
          maxWidth: 640,
          width: '100%',
          p: { xs: 3, md: 6 },
          borderRadius: 4,
          textAlign: 'center',
          boxShadow: '0 20px 40px rgba(0,0,0,0.06)',
        }}
      >
        <Stack spacing={4} alignItems='center'>
          {/* Success Icon */}
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                bgcolor: 'success.main',
                color: 'common.white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: (theme) => `0 8px 24px ${theme.palette.success.light}`,
              }}
            >
              <Check size={44} strokeWidth={3} />
            </Box>
            <Box
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                bgcolor: 'warning.light',
                color: 'warning.dark',
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 2,
              }}
            >
              <PartyPopper size={20} />
            </Box>
          </Box>

          {/* Congratulations Text */}
          <Stack spacing={1}>
            <Typography variant='h4' fontWeight={800} color='text.primary'>
              Congratulations{firstName ? `, ` : ' '}
              <Box
                component='span'
                sx={{
                  background: 'linear-gradient(45deg, #1976d2, #9c27b0)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {firstName || 'Candidate'}
              </Box>
              !
            </Typography>
            <Typography variant='h6' color='text.secondary' fontWeight={500}>
              You have successfully completed the assessment.
            </Typography>
          </Stack>

          {/* Success Message */}
          <Paper
            variant='outlined'
            sx={{
              p: 3,
              borderRadius: 3,
              bgcolor: 'action.hover',
              borderColor: 'primary.light',
              width: '100%',
            }}
          >
            <Typography variant='body1' color='text.primary'>
              Your responses have been recorded and submitted successfully.
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
              Thank you for your time and effort.
            </Typography>
          </Paper>

          {/* Action Button */}
          <Box sx={{ pt: 1 }}>
            <Button
              variant='outlined'
              color='inherit'
              size='large'
              onClick={logOut}
              sx={{ px: 4, py: 1.25, borderRadius: 2, fontWeight: 600 }}
            >
              Log Out
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};
